import React, { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import { httpOk, GET, POST, PUT } from 'helpers/api'

const emptyResult = {
	stats: null,
	catalog: [],
}

const FIELD_KEYS = [
	'name',
	'description',
	'weight',
	'proteins',
	'fat',
	'carbohydrates',
	'calories',
]

function displayValue(value) {
	if (value === null || value === undefined || value === '') return '—'
	return String(value)
}

function CellValue({ current, suggested, changed }) {
	if (!changed) {
		return <span>{displayValue(current)}</span>
	}

	return (
		<div className="ai-cell-changed">
			<div className="ai-cell-old">{displayValue(current)}</div>
			<div className="ai-cell-new">→ {displayValue(suggested)}</div>
		</div>
	)
}

function getField(product, key) {
	return (product.fields || []).find(field => field.key === key) || {
		key,
		current: product[key],
		suggested: null,
		changed: false,
	}
}

function buildFilterPayload(filterData = {}) {
	const payload = {}

	if (filterData.pos_id != null && filterData.pos_id !== '') {
		payload.pos_id = filterData.pos_id
	}
	if (filterData.wms_id != null && filterData.wms_id !== '') {
		payload.wms_id = filterData.wms_id
	}
	if (filterData.order_by) {
		payload.order_by = filterData.order_by
	}
	if (filterData.status != null && filterData.status !== '') {
		payload.status = filterData.status
	}
	if (filterData.product_without) {
		payload.product_without = filterData.product_without
	}
	if (filterData.category_id) {
		payload.category_id = filterData.category_id
	}
	if (filterData.menu_ids && filterData.menu_ids.length > 0) {
		payload.menu_ids = filterData.menu_ids.map(item => item.id).join(',')
	}
	if (filterData.preparation_place_id) {
		payload.preparation_place_id = filterData.preparation_place_id
	}
	if (filterData.menu_id) {
		payload.menu_id = filterData.menu_id
	}
	if (filterData.in_product != null && filterData.in_product !== '') {
		payload.in_product = filterData.in_product
	}
	if (filterData.from_profit_percent) {
		payload.from_profit_percent = filterData.from_profit_percent
	}
	if (filterData.to_profit_percent) {
		payload.to_profit_percent = filterData.to_profit_percent
	}
	if (filterData.search) {
		payload.search = filterData.search
	}

	return payload
}

function ProductsAiModal({ show, onHide, filterData, onApplied }) {
	const { t } = useTranslation()
	const [languages, setLanguages] = useState([])
	const [language, setLanguage] = useState(null)
	const [loading, setLoading] = useState(false)
	const [applying, setApplying] = useState(false)
	const [onlyChanges, setOnlyChanges] = useState(false)
	const [result, setResult] = useState(emptyResult)

	async function getLanguages() {
		const response = await GET('/helper/languages')
		if (httpOk(response)) {
			const list = response.data || []
			setLanguages(list)
			const ru = list.find(item => item.app_code === 'ru' || item.code === 'ru')
			setLanguage(ru || list[0] || null)
		}
	}

	function resetResult() {
		setResult(emptyResult)
	}

	async function runAi() {
		if (!language || !filterData?.pos_id) return

		setLoading(true)
		resetResult()

		const response = await POST('/products-ai', {
			language: language.app_code || language.code,
			...buildFilterPayload(filterData),
		}, { loader: true, notification: false, timeout: 150000 })

		setLoading(false)

		if (httpOk(response)) {
			const catalog = (response.data.catalog || []).map(product => ({
				...product,
				selected: !!product.has_changes,
			}))

			setResult({
				stats: response.data.stats || null,
				catalog,
			})
		}
	}

	function toggleProduct(productId) {
		setResult(prev => ({
			...prev,
			catalog: prev.catalog.map(product =>
				product.id === productId && product.has_changes
					? { ...product, selected: !product.selected }
					: product
			),
		}))
	}

	function selectAllChanges(selected) {
		setResult(prev => ({
			...prev,
			catalog: prev.catalog.map(product => ({
				...product,
				selected: product.has_changes ? selected : false,
			})),
		}))
	}

	function buildApplyPayload() {
		const products = []

		result.catalog.forEach(product => {
			if (!product.selected || !product.has_changes) return

			const row = { id: product.id }
			FIELD_KEYS.forEach(key => {
				const field = getField(product, key)
				if (field.changed && field.suggested != null) {
					row[key] = field.suggested
				}
			})

			if (Object.keys(row).length > 1) {
				products.push(row)
			}
		})

		return { products }
	}

	async function applyChanges() {
		const payload = buildApplyPayload()
		if (!payload.products.length) return

		setApplying(true)
		const response = await PUT('/products-ai', payload, {
			loader: true,
			notification: true,
		})
		setApplying(false)

		if (httpOk(response)) {
			onApplied?.()
			onHide?.()
		}
	}

	const selectedCount = useMemo(() => {
		return result.catalog.filter(product => product.selected && product.has_changes).length
	}, [result.catalog])

	const visibleCatalog = useMemo(() => {
		if (!onlyChanges) return result.catalog
		return result.catalog.filter(product => product.has_changes)
	}, [result.catalog, onlyChanges])

	useEffect(() => {
		if (show) {
			getLanguages()
			resetResult()
			setOnlyChanges(false)
		}
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Modal
			show={show}
			animation={false}
			centered
			size="xl"
			dialogClassName="ai-menu-modal"
			onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>
					AI · {t('products')}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<style>{`
					/* Окно по высоте экрана: таблица листается внутри, подвал с «Применить»
					   всегда виден. Раньше окно росло под таблицу, и на ноутбуке кнопка
					   уезжала за край — AI отрабатывал, а применить было нечем */
					.modal-dialog.ai-menu-modal {
						max-width: 1140px;
						height: calc(100vh - 2rem);
						margin: 1rem auto;
					}
					.ai-menu-modal .modal-content {
						height: 100%;
						display: flex;
						flex-direction: column;
					}
					.ai-menu-modal .modal-body {
						flex: 1 1 auto;
						display: flex;
						flex-direction: column;
						min-height: 0;
						overflow: hidden;
					}
					.ai-stats { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:12px; }
					.ai-stat { background:var(--dark-surface-2, #f4f6f9); border-radius:8px; padding:6px 10px; font-size:13px; }
					.ai-toolbar { display:flex; flex-wrap:wrap; gap:8px; align-items:center; margin-bottom:12px; }
					.ai-table-wrap {
						flex: 1 1 auto;
						min-height: 120px;
						overflow:auto;
						border:1px solid var(--dark-border, #dee2e6);
						border-radius:6px;
					}
					.ai-table { margin-bottom:0; font-size:13px; }
					.ai-table th {
						position:sticky; top:0; z-index:2; background:var(--dark-surface-2, #f8f9fa);
						white-space:nowrap; vertical-align:middle;
					}
					.ai-table td { vertical-align:middle; }
					.ai-row-ok td { background:var(--dark-surface, #fff); }
					.ai-row-fix td { background:var(--dark-tint-warning, #fff8db); }
					.ai-cell-old { color:var(--dark-text-muted, #6c757d); text-decoration:line-through; font-size:12px; }
					.ai-cell-new { color:var(--dark-fg-warning, #856404); font-weight:600; }
					.ai-badge {
						display:inline-block; border-radius:999px; padding:2px 8px;
						font-size:11px; font-weight:600; white-space:nowrap;
					}
					.ai-badge-ok { background:var(--dark-tint-success, #e7f8ef); color:var(--dark-fg-success, #16a34a); }
					.ai-badge-fix { background:var(--dark-tint-warning, #fff3cd); color:var(--dark-fg-warning, #856404); border:1px solid var(--dark-tint-border-warning, #ffe69c); }
					.ai-meta { color:var(--dark-text-muted, #6c757d); font-weight:400; font-size:12px; }
					.ai-bju { white-space:nowrap; font-size:12px; }
				`}</style>

				<div className="row g-2 mb-3">
					<div className="col-md-8">
						<label>Язык</label>
						<Select
							options={languages}
							value={language}
							onChange={setLanguage}
							getOptionLabel={(option) => option.native_name || option.name}
							getOptionValue={(option) => option.id}
							placeholder="Выберите язык"
							isClearable={false}
						/>
					</div>
					<div className="col-md-4 d-flex align-items-end">
						<button
							type="button"
							className="btn btn-primary w-100"
							disabled={!language || !filterData?.pos_id || loading}
							onClick={runAi}>
							{loading ? 'Обработка...' : 'Заполнить и проверить'}
						</button>
					</div>
				</div>

				<div className="mb-3 fz-14 text-muted">
					По текущему фильтру списка: заполняет пустые описание / вес / БЖУ и исправляет орфографию названия и описания.
					Жёлтый — правка, зелёный — ок.
				</div>

				{result.stats &&
					<div className="ai-stats">
						<div className="ai-stat"><strong>{result.stats.products}</strong> обработано</div>
						{result.stats.total_matched != null &&
							<div className="ai-stat"><strong>{result.stats.total_matched}</strong> в фильтре</div>
						}
						<div className="ai-stat"><strong>{result.stats.products_with_changes}</strong> с правками</div>
						<div className="ai-stat"><strong>{selectedCount}</strong> выбрано</div>
					</div>
				}

				{result.stats?.truncated &&
					<div className="alert alert-warning py-2 fz-13">
						За один запрос обработано до 24 товаров (сначала с пустыми полями).
						Сузьте фильтр или запустите AI ещё раз для остальных.
					</div>
				}

				{result.catalog.length > 0 &&
					<div className="ai-toolbar">
						<button type="button" className="btn btn-sm btn-outline-primary" onClick={() => selectAllChanges(true)}>
							Выбрать все правки
						</button>
						<button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => selectAllChanges(false)}>
							Снять все
						</button>
						<button
							type="button"
							className={`btn btn-sm ${onlyChanges ? 'btn-primary' : 'btn-outline-primary'}`}
							onClick={() => setOnlyChanges(v => !v)}>
							{onlyChanges ? 'Показать всё' : 'Только с правками'}
						</button>
					</div>
				}

				{visibleCatalog.length > 0 &&
					<div className="ai-table-wrap table-responsive">
						<table className="table table-bordered table-sm ai-table">
							<thead>
								<tr>
									<th style={{ width: 42 }} className="text-center">✓</th>
									<th style={{ width: 90 }}>Статус</th>
									<th style={{ minWidth: 180 }}>Название</th>
									<th style={{ minWidth: 220 }}>Описание</th>
									<th style={{ width: 90 }}>Вес</th>
									<th style={{ minWidth: 160 }}>БЖУ / ккал</th>
								</tr>
							</thead>
							<tbody>
								{visibleCatalog.map(product => {
									const nameField = getField(product, 'name')
									const descriptionField = getField(product, 'description')
									const weightField = getField(product, 'weight')
									const proteinsField = getField(product, 'proteins')
									const fatField = getField(product, 'fat')
									const carbField = getField(product, 'carbohydrates')
									const caloriesField = getField(product, 'calories')
									const bjuChanged = proteinsField.changed
										|| fatField.changed
										|| carbField.changed
										|| caloriesField.changed
									const rowClass = product.has_changes ? 'ai-row-fix' : 'ai-row-ok'

									return (
										<tr key={product.id} className={rowClass}>
											<td className="text-center">
												{product.has_changes ? (
													<input
														type="checkbox"
														checked={!!product.selected}
														onChange={() => toggleProduct(product.id)}
													/>
												) : null}
											</td>
											<td>
												{product.has_changes ? (
													<span className="ai-badge ai-badge-fix">правка</span>
												) : (
													<span className="ai-badge ai-badge-ok">ок</span>
												)}
											</td>
											<td>
												<CellValue
													current={nameField.current}
													suggested={nameField.suggested}
													changed={nameField.changed}
												/>
											</td>
											<td>
												<CellValue
													current={descriptionField.current}
													suggested={descriptionField.suggested}
													changed={descriptionField.changed}
												/>
											</td>
											<td>
												<CellValue
													current={weightField.current}
													suggested={weightField.suggested}
													changed={weightField.changed}
												/>
											</td>
											<td>
												{bjuChanged ? (
													<div className="ai-bju">
														<div className="ai-cell-old">
															Б{displayValue(proteinsField.current)} /
															Ж{displayValue(fatField.current)} /
															У{displayValue(carbField.current)} /
															{displayValue(caloriesField.current)} ккал
														</div>
														<div className="ai-cell-new">
															→ Б{displayValue(proteinsField.changed ? proteinsField.suggested : proteinsField.current)} /
															Ж{displayValue(fatField.changed ? fatField.suggested : fatField.current)} /
															У{displayValue(carbField.changed ? carbField.suggested : carbField.current)} /
															{displayValue(caloriesField.changed ? caloriesField.suggested : caloriesField.current)} ккал
														</div>
													</div>
												) : (
													<div className="ai-bju">
														Б{displayValue(proteinsField.current)} /
														Ж{displayValue(fatField.current)} /
														У{displayValue(carbField.current)} /
														{displayValue(caloriesField.current)} ккал
													</div>
												)}
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>
				}

				{!loading && result.stats && result.catalog.length === 0 &&
					<div className="text-muted">По текущему фильтру товары не найдены.</div>
				}
			</Modal.Body>
			<Modal.Footer className="d-flex justify-content-between">
				<div className="text-muted fz-13">
					{selectedCount > 0 ? `К применению: ${selectedCount}` : 'Выберите правки галочками'}
				</div>
				<div className="d-flex gap-2">
					<button type="button" className="btn btn-outline-warning" onClick={onHide}>
						{t('cancel')}
					</button>
					<button
						type="button"
						className="btn btn-primary"
						disabled={selectedCount === 0 || applying}
						onClick={applyChanges}>
						Применить ({selectedCount})
					</button>
				</div>
			</Modal.Footer>
		</Modal>
	)
}

export default ProductsAiModal
